import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { AppNotificationAlert, AppTextArea } from '@/layout/AppComponents';
import { axiosPath } from '../../../../routing/helpers/router';
import useIsMounted from '../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import minions from '../../../../minions/minions';
import colors from '../../../../constants/colors';
import * as currentPageAction from '../../../../store/actions/currentPageAction';

export default function PendingItemListPublicationInfo({ element, onClose }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level
  );
  const [validationSchema] = useState(
    Yup.object().shape({
      validated: Yup.boolean().required(),
      comment: Yup.string().when("validated", ([validated], schema) => {
        if (validated === false) {
            return schema.required('Debes introducir un comentario').nullable();
        }
            return schema.notRequired();
      }),
    })
  );

  const handleSave = useCallback(
    async (v) =>
      axios
        .post(
          axiosPath("api_r_user_module_documentManager_pendingItem_validation"),
          {
            moduleBridgeHex,
            itemHex: element?.hex,
            ...v,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change("loading", true));
            onClose();
            AppNotificationAlert({
              title: "Completado",
              message: data?.validated
                ? "La publicación ha sido validada"
                : "La publicación ha sido rechazada",
              type: "success",
              duration: 10000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, element]
  );

  const handleDownload = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_documentManager_item_publication_download"
          ),
          {
            moduleBridgeHex,
            itemHex: element?.hex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            window.open(data, "_blank");
            onClose();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [element]
  );

  return (
    <>
      {permissionsLevel === 1 && (
        <div className="row">
          <div className="col-lg-12">
            <div className="alert alert-warning">
              La publicación está pendiente de validación
            </div>
          </div>
        </div>
      )}
      <div className="row">
        <div className="col-lg-6">
          <h6>Título de la publicación</h6>
          <div className="mb-3">{element?.title}</div>
          <h6>Resumen del contenido</h6>
          <div className="mb-3">{element?.description || '--'}</div>
          <h6>Autor o institución emisora de la publicación</h6>
          <div className="mb-3">
            {element?.issuer || '--'}
          </div>
          <h6>Año de publicación / creación / edición</h6>
          <div className="mb-3">{element?.publicationYear || '--'}</div>
          <h6>Diario o periódico oficial donde fue publicado</h6>
          <div className="mb-3">
            {moduleData?.inputs?.publicationLocations?.find(
              (i) => i?.value === element?.publicationLocation
            )?.label || '--'}
          </div>
          <h6>Fecha de publicación</h6>
          <div className="mb-3">{element?.publicationDateStamp || '--'}</div>
          <h6>Fecha de aportación</h6>
          <div className="mb-3">{element?.dateStamp || '--'}</div>
          <h6>Tipo de publicación</h6>
          <div className="mb-3">
            {moduleData?.inputs?.publicationTypes?.find(
              (i) => i?.value === element?.publicationType
            )?.label || '--'}
          </div>
          <h6>Recurso aportado por</h6>
          <div className="mb-3">
            {moduleData?.inputs?.userBridges?.find(
              (i) => i?.value === element?.userBridge
            )?.label || '--'}
          </div>
          <h6>Archivo asociado</h6>
          {element?.fileName ? (
            <div className="mb-3">
              <File class onClick={handleDownload} title="Descargar">
                {minions.files.fileIcon(element?.fileName)} {element?.fileName}
              </File>
            </div>
          ) : (
            <div className="alert alert-info">No existe archivo asociado</div>
          )}
        </div>
        <div className="col-lg-6">
          <h5>Clasificación</h5>
          <h6>Temática principal</h6>
          <div className="mb-3">
            {moduleData?.inputs?.mainSubjects?.find(
              (i) => i?.value === element?.mainSubject
            )?.label || '--'}
          </div>
          <h6>Temáticas secundarias</h6>
          <div className="mb-3">
            {element?.secondarySubjects?.length > 0 ? (
              <ul>
                {element?.secondarySubjects?.map((ss) => (
                  <li key={ss}>
                    {moduleData?.inputs?.secondarySubjects?.find(
                      (i) => i?.value === ss
                    )?.label || '--'}
                  </li>
                ))}
              </ul>
            ) : (
              '--'
            )}
          </div>
          <h5>Ubicación geográfica</h5>
          <h6>Localizaciones</h6>
          <div className="mb-3">
            {element?.locations?.length > 0 ? (
              <ul>
                {element?.locations?.map((l) => (
                  <li key={l?.value}>{l?.label}</li>
                ))}
              </ul>
            ) : (
              '--'
            )}
          </div>
          <h6>Región natural</h6>
          <div className="mb-3">
            {element?.naturalRegions?.length > 0 ? (
              <ul>
                {element?.naturalRegions?.map((nr) => (
                  <li key={nr?.id}>{nr?.label}</li>
                ))}
              </ul>
            ) : (
              '--'
            )}
          </div>
          <h6>Coordenadas</h6>
          <div className="mb-3">{element?.coordinates || '--'}</div>
          <h6>Palabras clave</h6>
          <div className="mb-3">
            {console.log(element)}
            {element?.tags?.length > 0
              ? element?.tags?.map((t) => (
                  <span
                    key={t?.id}
                    className="badge badge-xs ml-1 pl-1 pr-1"
                    style={{
                      backgroundColor: colors.paletteColors[t?.color],
                      color: '#fff',
                    }}
                  >
                    {t?.label}
                  </span>
                ))
              : '--'}
          </div>
        </div>
      </div>
      {permissionsLevel === 0 && (
        <Formik
          initialValues={{
            comment: '',
            validated: false,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(values) => handleSave(values)}
        >
          {({ setFieldValue, errors, handleSubmit, values, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-12">
                  <AppTextArea
                    title="Comentario (sólo para rechazar)"
                    onChange={(value) => setFieldValue('comment', value)}
                    value={values.comment}
                    error={errors.comment}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="float-right">
                    <button
                      type="submit"
                      className="btn btn-danger mr-2"
                      onClick={() => setFieldValue('validated', false)}
                      disabled={isSubmitting}
                    >
                      Rechazar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      onClick={() => setFieldValue('validated', true)}
                      disabled={isSubmitting}
                    >
                      Validar
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      )}
    </>
  );
}

const File = styled.span`
  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;
